import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MetricCard from './MetricCard';
import { TrendingUp, Users, AlertTriangle, Clock, Target, Zap } from "lucide-react";

// Dynamic metric configuration based on project type
const getMetricConfig = (isTrello = false, isAzureBoards = false) => [
  {
    id: "prsUsed",
    title: "Total PRs Analyzed (Team Velocity)",
    subtitle: "Team Velocity",
    icon: <Users />,
    iconBgColor: "#fff",
    getValue: ({ stats }) => stats?.totalPRsAnalyzed || 0,
    getTrendPct: ({ stats }) => stats?.totalPRsAnalyzedTrendPct,
    getDescription: () => "Pull Requests Reviewed",
    trendTextTemplate: "faster team velocity",
  },
  {
    id: isTrello ? "totalTrelloAnalyzed" : (isAzureBoards ? "totalAzureBoardsAnalyzed" : "totalJirasAnalyzed"),
    title: isTrello ? "Cards Analyzed (Trello Coverage)" : (isAzureBoards ? "Work Items Analyzed (Azure Boards Coverage)" : "Stories Analyzed (JIRA Coverage)"),
    subtitle: isTrello ? "Trello Coverage" : (isAzureBoards ? "Azure Boards Coverage" : "JIRA Coverage"),
    icon: <Target />,
    iconBgColor: "#fff",
    getValue: ({ stats }) => {
      if (isTrello) return stats?.totalTrelloAnalyzed || 0;
      if (isAzureBoards) return stats?.totalAzureBoardsAnalyzed || 0;
      return stats?.totalJirasAnalyzed || 0;
    },
    getTrendPct: ({ stats }) => {
      if (isTrello) return stats?.totalTrelloAnalyzedTrendPct;
      if (isAzureBoards) return stats?.totalAzureBoardsAnalyzedTrendPct;
      return stats?.totalJirasAnalyzedTrendPct;
    },
    getDescription: () => {
      if (isTrello) return "Cards Processed";
      if (isAzureBoards) return "Work Items Processed";
      return "Tickets Processed";
    },
    trendTextTemplate: isTrello ? "more Trello cards analyzed" : (isAzureBoards ? "more Azure Boards analyzed" : "more JIRAs analyzed"),
  },
  {
    id: "totalIssues",
    title: "Issues Detected",
    subtitle: "Quality Metrics",
    icon: <AlertTriangle />,
    iconBgColor: "#fff",
    getValue: ({ totalIssues }) => totalIssues,
    getTrendPct: ({ stats }) => stats?.totalIssuesTrendPct,
    getDescription: ({ avgIssuesPerPR }) => `${avgIssuesPerPR} per PR average`,
    trendTextTemplate: "quality improving",
  },
  {
    id: "avgReviewTime",
    title: "Avg PR Mean Time to Merge",
    subtitle: "Review Efficiency",
    icon: <Clock />,
    iconBgColor: "#fff",
    getValue: ({ stats }) => stats?.avgReviewTime ? Number(stats.avgReviewTime).toFixed(2) : "0.00",
    getTrendPct: ({ stats }) => stats?.avgReviewTimeTrendPct,
    getDescription: () => "Per Pull Request",
    trendTextTemplate: "reviews getting quicker",
  },
  {
    id: "averageJiraAccuracy",
    title: "Planned vs Delivered Accuracy",
    subtitle: "Alignment Metrics",
    icon: <TrendingUp />,
    iconBgColor: "#fff",
    getValue: ({ stats }) => {
      const accuracy = isTrello
        ? (Number(stats?.trelloAccuracy) || 0)
        : (isAzureBoards 
          ? (Number(stats?.azureBoardsAccuracy) || 0)
          : (Number(stats?.jiraAccuracy) || 0));
      return `${accuracy.toFixed(1)}%`;
    },
    getTrendPct: ({ stats }) => isTrello
      ? stats?.trelloAccuracyTrendPct
      : (isAzureBoards 
        ? stats?.azureBoardsAccuracyTrendPct
        : stats?.jiraAccuracyTrendPct),
    getDescription: () => "Ticket Correlation Rate",
    trendTextTemplate: "alignment strengthening",
  },
  {
    id: "cycleTime",
    title: isTrello ? "Cycle Time (Trello → Delivery)" : (isAzureBoards ? "Cycle Time (Azure Boards → Delivery)" : "Cycle Time (JIRA → Delivery)"),
    subtitle: isTrello ? "Trello → Delivery" : (isAzureBoards ? "Azure Boards → Delivery" : "JIRA → Delivery"),
    icon: <Zap />,
    iconBgColor: "#fff",
    getValue: ({ stats }) => {
      const cycleTime = isTrello ? stats?.trelloCycleTime : (isAzureBoards ? stats?.azureBoardsCycleTime : stats?.jiraCycleTime);
      if (!cycleTime) return "N/A";
      const value = Number(cycleTime);
      if (value === 0 || value < 0.05) return "N/A";
      return `${value.toFixed(1)}h`;
    },
    getTrendPct: ({ stats }) => isTrello ? stats?.trelloCycleTimeTrendPct : (isAzureBoards ? stats?.azureBoardsCycleTimeTrendPct : stats?.jiraCycleTimeTrendPct),
    getDescription: () => "Average Development Cycle",
    trendTextTemplate: "needs attention",
  },
];

const calculateTrend = (current, previous) => {
  if (previous === undefined || previous === null || current === undefined || current === null) {
    return { change: 'N/A', trend: 'neutral', trendText: 'No historical data' };
  }

  const currentVal = typeof current === 'string' ? parseFloat(current) : Number(current);
  const previousVal = typeof previous === 'string' ? parseFloat(previous) : Number(previous);

  if (isNaN(currentVal) || isNaN(previousVal) || previousVal === 0) {
    return { change: 'N/A', trend: 'neutral', trendText: 'No historical data' };
  }

  const diff = currentVal - previousVal;
  const percentageChange = (diff / previousVal) * 100;

  if (Math.abs(percentageChange) < 0.1) {
    return { change: '0.0%', trend: 'neutral', trendText: 'No significant change' };
  }

  const trend = percentageChange > 0 ? 'up' : 'down';
  const change = `${percentageChange > 0 ? '+' : ''}${percentageChange.toFixed(1)}%`;

  return { change, trend };
};

const generateFooterText = (metricConfig, currentValue, previousValue, backendData, trendChange) => {
  const { id, trendTextTemplate } = metricConfig;
  const { delta, hasPrev } = backendData || {};

  const fmt = (x) => {
    if (id === 'avgReviewTime') return `${x.toFixed(2)}h`;
    if (id === 'averageJiraAccuracy') return `${x.toFixed(1)}%`;
    if (id === 'cycleTime') return `${x.toFixed(1)}h`;
    return `${x.toFixed(0)}`;
  };

  let hasPreviousData = false;
  let deltaVal;

  if (hasPrev) { // Use backend data if available
    hasPreviousData = true;
    deltaVal = Number(delta || 0);
  } else if (previousValue !== undefined && previousValue !== null && !isNaN(previousValue)) { // Fallback to historical
    const currNum = typeof currentValue === 'string' ? parseFloat(currentValue) : Number(currentValue);
    const prevNum = typeof previousValue === 'string' ? parseFloat(previousValue) : Number(previousValue);
    if (!isNaN(currNum) && !isNaN(prevNum)) {
      hasPreviousData = true;
      deltaVal = currNum - prevNum;
    }
  }

  if (hasPreviousData) {
    if (deltaVal === 0) {
      return 'No change vs previous period';
    }
    const absDelta = Math.abs(deltaVal);
    const isTimeMetric = id === 'avgReviewTime' || id === 'cycleTime';

    if (isTimeMetric) {
      const qualifier = deltaVal > 0 ? 'more hours' : 'fewer hours';
      return `${fmt(absDelta)} ${qualifier} vs previous period`;
    } else {
      const signDelta = deltaVal > 0 ? '+' : deltaVal < 0 ? '' : '+';
      return `Δ ${signDelta}${fmt(absDelta)} vs previous period`;
    }
  }

  // Fallback text if no delta can be calculated
  if (trendChange && trendChange !== 'N/A') {
    const num = parseFloat(trendChange);
    if (!isNaN(num)) {
      return `${Math.abs(num).toFixed(1)}% — ${trendTextTemplate}`;
    }
  }

  return 'No previous data';
};

const SectionContainer = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  marginBottom: '8px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
}));

const EngineeringHealth = ({ stats, subscription, issueTrends, averageJiraAccuracy, historicalStats = {}, projects = [], selectedProject = "" }) => {
  // Determine if current project uses Azure Boards or Trello
  const currentProject = projects.find(p => p.api_key === selectedProject);
  const isAzureBoards = currentProject?.azure_boards_enabled || false;
  const isTrello = currentProject?.trello_enabled || false;
  
  // Get dynamic metric configuration
  const METRIC_CONFIG = getMetricConfig(isTrello, isAzureBoards);
  // Calculate derived metrics
  const totalIssues = useMemo(() => (issueTrends?.critical || []).reduce((sum, item) => sum + item.count, 0) +
                     (issueTrends?.medium || []).reduce((sum, item) => sum + item.count, 0) +
                     (issueTrends?.low || []).reduce((sum, item) => sum + item.count, 0), [issueTrends]);
  
  const avgIssuesPerPR = useMemo(() => {
    const prs = (stats?.totalPRsAnalyzed ?? 0);
    return prs > 0 ? (totalIssues / prs).toFixed(1) : '0.0';
  }, [totalIssues, stats]);

  const engineeringMetrics = useMemo(() => {
    const dataContext = {
      stats,
      subscription,
      issueTrends,
      averageJiraAccuracy,
      totalIssues,
      avgIssuesPerPR,
    };

    // Dynamic mapping based on project type (JIRA vs Azure Boards vs Trello)
    const analyzedMetricId = isTrello ? 'totalTrelloAnalyzed' : (isAzureBoards ? 'totalAzureBoardsAnalyzed' : 'totalJirasAnalyzed');
    const accuracyMetricId = 'averageJiraAccuracy'; // Same ID for all, but different data sources
    const cycleTimeMetricId = 'cycleTime'; // Same ID for all, but different data sources

    const mapIdToDelta = {
      prsUsed: {
        delta: stats?.totalPRsAnalyzedDelta,
        hasPrev: stats?.totalPRsAnalyzedHasPrev,
      },
      [analyzedMetricId]: {
        delta: isTrello 
          ? stats?.totalTrelloAnalyzedDelta 
          : (isAzureBoards 
            ? stats?.totalAzureBoardsAnalyzedDelta 
            : stats?.totalJirasAnalyzedDelta),
        hasPrev: isTrello 
          ? stats?.totalTrelloAnalyzedHasPrev 
          : (isAzureBoards 
            ? stats?.totalAzureBoardsAnalyzedHasPrev 
            : stats?.totalJirasAnalyzedHasPrev),
      },
      totalIssues: {
        delta: stats?.totalIssuesDelta,
        hasPrev: stats?.totalIssuesHasPrev,
      },
      avgReviewTime: {
        delta: stats?.avgReviewTimeDelta,
        hasPrev: stats?.avgReviewTimeHasPrev,
      },
      [accuracyMetricId]: {
        delta: isTrello 
          ? stats?.trelloAccuracyDelta 
          : (isAzureBoards 
            ? stats?.azureBoardsAccuracyDelta 
            : stats?.jiraAccuracyDelta),
        hasPrev: isTrello 
          ? stats?.trelloAccuracyHasPrev 
          : (isAzureBoards 
            ? stats?.azureBoardsAccuracyHasPrev 
            : stats?.jiraAccuracyHasPrev),
      },
      [cycleTimeMetricId]: {
        delta: isTrello 
          ? stats?.trelloCycleTimeDelta 
          : (isAzureBoards 
            ? stats?.azureBoardsCycleTimeDelta 
            : stats?.jiraCycleTimeDelta),
        hasPrev: isTrello 
          ? stats?.trelloCycleTimeHasPrev 
          : (isAzureBoards 
            ? stats?.azureBoardsCycleTimeHasPrev 
            : stats?.jiraCycleTimeHasPrev),
      }
    };

    return METRIC_CONFIG.map(metricConfig => {
      const currentValue = metricConfig.getValue(dataContext);
      const trendPct = typeof metricConfig.getTrendPct === 'function' ? metricConfig.getTrendPct(dataContext) : undefined;

      // Infer no-current-data for key time/accuracy metrics (when previous exists)
      const cfg = mapIdToDelta[metricConfig.id];
      const hasPrevFlag = !!cfg?.hasPrev;
      const toNum = (v) => {
        if (v === null || v === undefined) return 0;
        const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
        return Number.isFinite(n) ? n : 0;
      };
      let noCurrentData = false;
      if (metricConfig.id === 'averageJiraAccuracy') {
        const accuracyValue = isTrello
          ? toNum(stats?.trelloAccuracy)
          : (isAzureBoards 
            ? toNum(stats?.azureBoardsAccuracy) 
            : toNum(stats?.jiraAccuracy));
        noCurrentData = hasPrevFlag && accuracyValue === 0;
      } else if (metricConfig.id === 'cycleTime') {
        const cycleTimeValue = isTrello
          ? toNum(stats?.trelloCycleTime)
          : (isAzureBoards 
            ? toNum(stats?.azureBoardsCycleTime) 
            : toNum(stats?.jiraCycleTime));
        // Show "—" if there's previous data but current value is 0 or negligible
        noCurrentData = hasPrevFlag && (cycleTimeValue === 0 || cycleTimeValue < 0.05);
      } else if (metricConfig.id === 'avgReviewTime') {
        noCurrentData = hasPrevFlag && toNum(stats?.avgReviewTime) === 0;
      }

      // Prefer backend-provided trend pct; fallback to historicalStats if absent
      let change = undefined;
      let trend = undefined;
      let trendText = undefined;
      let effectiveValue = currentValue;

      if (noCurrentData) {
        effectiveValue = '—';
        change = undefined;
        trend = undefined;
        trendText = 'No data in selected period';
      } else if (typeof trendPct === 'number') {
        let val = Number(trendPct) || 0;
        if (val > 300) val = 300;
        if (val < -300) val = -300;
        const sign = val > 0 ? '+' : val < 0 ? '' : '+';
        change = `${sign}${Math.abs(val).toFixed(1)}%`;
        trend = val > 0 ? 'up' : val < 0 ? 'down' : 'up';
        trendText = generateFooterText(metricConfig, currentValue, undefined, cfg, change);
      } else {
        const previousValue = historicalStats[metricConfig.id];
        const fallback = calculateTrend(currentValue, previousValue);
        // clamp fallback change value to ±300%
        if (fallback.change && fallback.change.endsWith('%')) {
          const raw = parseFloat(fallback.change);
          const capped = isNaN(raw) ? 0 : Math.max(-300, Math.min(300, raw));
          const sign = capped > 0 ? '+' : capped < 0 ? '' : '+';
          change = `${sign}${Math.abs(capped).toFixed(1)}%`;
        } else {
          change = fallback.change;
        }
        trend = fallback.trend;
        trendText = generateFooterText(metricConfig, currentValue, previousValue, undefined, change);
      }

      const invertColors = metricConfig.id === 'cycleTime' || metricConfig.id === 'avgReviewTime' || metricConfig.id === 'totalIssues';
      return {
        title: metricConfig.title,
        subtitle: metricConfig.subtitle,
        value: effectiveValue,
        description: metricConfig.getDescription(dataContext),
        icon: metricConfig.icon,
        change,
        trend,
        trendText,
        iconBgColor: metricConfig.iconBgColor,
        invertColors,
      };
    });
  }, [stats, subscription, issueTrends, averageJiraAccuracy, historicalStats, totalIssues, avgIssuesPerPR, isAzureBoards, METRIC_CONFIG]);

  return (
    <SectionContainer>
      <SectionHeader>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#020817',
            fontSize: '1.5rem',
            lineHeight: '2rem',
          }}
        >
          Engineering Health
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: '#6b7280',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          }}
        >
          Real-time development metrics
        </Typography>
      </SectionHeader>

      <Grid container spacing={3}>
        {engineeringMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} lg={2} xl={2} key={metric.title} sx={{ display: 'flex' }}>
            <MetricCard
              title={metric.title}
              value={metric.value}
              description={metric.description}
              icon={metric.icon}
              change={metric.change}
              trend={metric.trend}
              trendText={metric.trendText}
              iconBgColor={metric.iconBgColor}
              invertColors={metric.invertColors}
              sx={{ width: '100%' }}
            />
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );
};

export default EngineeringHealth;